/**
 * The Museum tab (docs/fossil-eras-plan.md R3): what fossils buy.
 *
 * The same icon-only tiles as the upgrades and the shard shop, with name,
 * price and effect in the tooltip, and an Owned drawer underneath. The
 * balance line says what the held fossils are worth in production, because
 * that is exactly what a purchase spends.
 */

import { PERKS } from '../data/museum';
import type { PerkDef } from '../data/museum';
import { FOSSIL_BONUS } from '../data/eras';
import { hasPerk, isPerkAvailable } from '../game/rebirth';
import { formatEggs } from '../game/numbers';
import type { GameState } from '../game/state';
import { bindTooltip } from './tooltip';
import { addRowText } from './itemView';
import { createDrawer, drawerElement } from './drawer';
import { createCollectionView } from './collection';
import { createHistoryView } from './history';

export interface MuseumView {
  update(state: GameState): void;
}

interface Tile {
  def: PerkDef;
  button: HTMLButtonElement;
  owned: HTMLElement;
  lastVisible: boolean | null;
  lastAffordable: boolean | null;
  lastOwned: boolean | null;
}

/** Each perk's 48px icon in public/art/icons/museum/, named after the id's second half. */
function iconUrl(def: PerkDef): string {
  return `${import.meta.env.BASE_URL}art/icons/museum/${def.id.split(':')[1]}.png`;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function iconImage(def: PerkDef): HTMLImageElement {
  const img = document.createElement('img');
  img.className = 'upgrade__icon';
  img.alt = '';
  img.src = iconUrl(def);
  return img;
}

function buildTile(def: PerkDef, onBuy: (def: PerkDef) => void): Tile {
  const button = el('button', 'upgrade');
  button.type = 'button';
  button.hidden = true;
  button.setAttribute('aria-label', `${def.name}, ${def.cost} fossils: ${def.description}`);
  button.append(iconImage(def));
  addRowText(button, def.name, def.description, {
    currency: 'fossils',
    amount: String(def.cost),
  });
  button.addEventListener('click', () => onBuy(def));
  bindTooltip(button, () => ({
    icon: { src: iconUrl(def) },
    lines: [
      { text: def.name },
      { text: `${def.cost} fossil${def.cost === 1 ? '' : 's'}`, cost: true },
      { text: def.description, muted: true },
    ],
  }));

  const owned = el('div', 'upgrade upgrade--owned');
  owned.tabIndex = 0;
  owned.hidden = true;
  owned.setAttribute('aria-label', `${def.name} (owned): ${def.description}`);
  owned.append(iconImage(def));
  bindTooltip(owned, () => ({
    icon: { src: iconUrl(def) },
    lines: [{ text: def.name }, { text: def.description, muted: true }],
  }));

  return { def, button, owned, lastVisible: null, lastAffordable: null, lastOwned: null };
}

/** Shown until an era has been ended, when "fossil" is still only a word. */
const INTRO =
  'Fossils come from ending an era. Each one you keep raises production; each one you spend here buys something permanent instead.';

/** Shown once there is nothing left to buy. */
const ALL_BOUGHT = 'Every perk is bought. Fossils from here on are worth keeping, not spending.';

export function createMuseumView(root: HTMLElement, onBuy: (def: PerkDef) => void): MuseumView {
  const balance = el('p', 'museum__balance');
  const count = el('span', 'museum__count', '0');
  const worth = el('span', 'museum__worth');
  const unit = el('span', '', ' fossils ');
  balance.append(count, unit, worth);

  const empty = el('p', 'upgrades__empty');
  const list = el('div', 'upgrades__list');
  const ownedDrawer = createDrawer('Owned', 'museum');
  // A museum is what has been, not only what is for sale: the era log lives
  // here rather than on the Stats page, where one line each had no room for
  // the dinosaur the era is remembered by.
  // The collection sits above the history: what the Museum holds comes
  // before the account of how it got there.
  const collectionDrawer = createDrawer('Collection', 'collection');
  const collection = createCollectionView();
  collectionDrawer.list.append(collection.element);
  const historyDrawer = createDrawer('History', 'history');
  const history = createHistoryView();
  historyDrawer.list.append(history.element);
  root.replaceChildren(
    balance,
    empty,
    list,
    drawerElement(ownedDrawer),
    drawerElement(collectionDrawer),
    drawerElement(historyDrawer),
  );

  const tiles = PERKS.map((def) => buildTile(def, onBuy));
  for (const t of tiles) {
    list.append(t.button);
    ownedDrawer.list.append(t.owned);
  }

  let lastBalance = '';
  // Null, not '': the note starts empty, and an initial '' would match and
  // skip the first write, leaving the paragraph unhidden and holding space.
  let lastNote: string | null = null;
  let lastAnyOwned: boolean | null = null;

  return {
    update(state) {
      const text = formatEggs(state.fossils);
      if (text !== lastBalance) {
        lastBalance = text;
        count.textContent = text;
        unit.textContent = state.fossils === 1 ? ' fossil ' : ' fossils ';
        worth.textContent = `(+${Math.round(state.fossils * FOSSIL_BONUS * 100)}% production)`;
      }

      let anyVisible = false;
      let ownedCount = 0;
      for (const t of tiles) {
        const owned = hasPerk(state, t.def.id);
        if (owned) ownedCount++;
        if (owned !== t.lastOwned) {
          t.lastOwned = owned;
          t.owned.hidden = !owned;
        }

        const visible = isPerkAvailable(t.def, state);
        anyVisible ||= visible;
        if (visible !== t.lastVisible) {
          t.lastVisible = visible;
          t.button.hidden = !visible;
        }
        if (!visible) continue;
        const affordable = state.fossils >= t.def.cost;
        if (affordable !== t.lastAffordable) {
          t.lastAffordable = affordable;
          t.button.disabled = !affordable;
        }
      }

      // The Museum opens before the first extinction now, so its first
      // visitor has never held a fossil: the tiles say what the perks cost
      // but nothing says where the currency comes from. Once every perk is
      // bought there is no list left to explain, so it says that instead.
      const note = state.fossilsEarned === 0 ? INTRO : anyVisible ? '' : ALL_BOUGHT;
      if (note !== lastNote) {
        lastNote = note;
        empty.textContent = note;
        empty.hidden = note === '';
      }
      const anyOwned = ownedCount > 0;
      if (anyOwned !== lastAnyOwned) {
        lastAnyOwned = anyOwned;
        ownedDrawer.setPresent(anyOwned);
      }
      ownedDrawer.setCount(ownedCount);

      collection.update(state);
      collectionDrawer.setPresent(state.eraLog.length > 0);
      collectionDrawer.setCount(collection.filled);

      history.update(state);
      historyDrawer.setPresent(state.eraLog.length > 0);
      historyDrawer.setCount(state.eraLog.length);
    },
  };
}
